import React from 'react';
import { NavLink, NavigationType, useLocation, useNavigationType, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import CreditBadge from '../components/CreditBadge';
import NotificationBell from '../components/NotificationBell';
import { resetConsent, useCookieConsent } from '../utils/cookieConsent';

export default function MainLayout(): React.JSX.Element {
  // When the cookie banner is showing (no stored consent) it floats above the
  // bottom edge, so reserve extra space below the content — otherwise it would
  // cover the in-page legal/policy links.
  const cookieBannerVisible = useCookieConsent() === null;
  return (
    <div className="min-h-screen bg-[var(--c-cream)] text-[var(--ink)] font-text flex">
      {/* macOS-style sidebar (desktop) */}
      <aside
        className="hidden md:flex w-64 shrink-0 flex-col justify-between p-6 sticky top-0 h-screen overflow-y-auto"
        style={{
          zIndex: 1,
          background: 'rgba(248,244,238,0.92)',
          backdropFilter: 'blur(12px) saturate(1.1)',
          WebkitBackdropFilter: 'blur(12px) saturate(1.1)',
          borderRight: '1px solid var(--rule)',
        }}
      >
        <div>
          <div className="mb-10 flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
            <Brand />
            <div className="flex items-center gap-2">
              <NotificationBell />
              <CreditBadge />
            </div>
          </div>
          <nav aria-label="Primary" className="flex flex-col gap-1">
            <SideItem to="/"          label="Home"         icon="home" />
            <SideItem to="/new"       label="New Analysis" icon="grid_view" />
            <SideItem to="/favorites" label="Favorites"    icon="favorite" />
            <SideItem to="/history"   label="History"      icon="history" />
            <SideItem to="/wallet"    label="Credits"      icon="account_balance_wallet" />
            <SideItem to="/profile"   label="Profile"      icon="person" />
            <SideItem to="/support"   label="Support"      icon="help" />
          </nav>
        </div>

        <div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 px-1" style={{ fontSize: 11, color: 'var(--ink-muted)' }}>
            <NavLink to="/terms"        className="hover:underline">Terms</NavLink>
            <span>•</span>
            <NavLink to="/privacy"      className="hover:underline">Privacy</NavLink>
            <span>•</span>
            <NavLink to="/cookies"      className="hover:underline">Cookies</NavLink>
            <span>•</span>
            <NavLink to="/data-retention" className="hover:underline">Data retention</NavLink>
            <span>•</span>
            <NavLink to="/refund"       className="hover:underline">Refund</NavLink>
            <span>•</span>
            <NavLink to="/ai-disclaimer" className="hover:underline">AI</NavLink>
            <span>•</span>
            <NavLink to="/photo-consent" className="hover:underline">Biometric</NavLink>
            <span>•</span>
            <NavLink to="/affiliate-disclosure" className="hover:underline">Affiliate</NavLink>
            <span>•</span>
            <button
              onClick={resetConsent}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}
              className="hover:underline"
            >
              Manage cookies
            </button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 overflow-x-hidden flex flex-col" style={{ position: 'relative', zIndex: 1 }}>
        {/* Brand bar — mobile only */}
        <header
          className="md:hidden sticky top-0 z-40"
          style={{
            background: 'rgba(248,244,238,0.88)',
            backdropFilter: 'blur(12px) saturate(1.1)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.1)',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <div className="max-w-5xl mx-auto h-14 px-4 flex items-center justify-between">
            <Brand />
            <div className="flex items-center gap-2">
              <NotificationBell />
              <CreditBadge />
            </div>
          </div>
        </header>

        {/* Content */}
        <main className={`min-w-0 overflow-x-hidden px-2 sm:px-4 md:px-6 py-4 md:py-10 ${cookieBannerVisible ? 'pb-[15.5rem] md:pb-32' : 'pb-[7.5rem] md:pb-12'}`}>
          <div className="w-full min-w-0 max-w-5xl mx-auto overflow-x-hidden">
            <AnimatedOutlet />
            {/* Mobile legal strip */}
            <div className="md:hidden mt-4">
              <div className="text-[11px] flex flex-wrap items-center justify-center gap-x-2 gap-y-1" style={{ color: 'var(--ink-muted)' }}>
                <NavLink to="/terms"   className="underline underline-offset-2">Terms</NavLink>
                <span>•</span>
                <NavLink to="/privacy" className="underline underline-offset-2">Privacy</NavLink>
                <span>•</span>
                <NavLink to="/cookies" className="underline underline-offset-2">Cookies</NavLink>
                <span>•</span>
                <NavLink to="/data-retention" className="underline underline-offset-2">Data retention</NavLink>
                <span>•</span>
                <NavLink to="/refund"  className="underline underline-offset-2">Refund</NavLink>
                <span>•</span>
                <NavLink to="/affiliate-disclosure" className="underline underline-offset-2">Affiliate</NavLink>
                <span>•</span>
                <NavLink to="/support" className="underline underline-offset-2">Support</NavLink>
                <span>•</span>
                <button
                  onClick={resetConsent}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}
                  className="underline underline-offset-2"
                >
                  Manage cookies
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* iOS-style bottom tab bar — mobile only */}
        <nav
          aria-label="Primary"
          className="kv-tab-bar md:hidden fixed bottom-0 left-0 right-0"
          style={{
            background: 'rgba(248,244,238,0.92)',
            backdropFilter: 'blur(12px) saturate(1.1)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.1)',
            borderTop: '1px solid var(--rule)',
            zIndex: 40,
          }}
        >
          <div className="h-14 max-w-xl mx-auto px-6 flex items-center justify-between text-xs">
            <TabItem to="/"          label="Home"      icon={HomeIcon} />
            <TabItem to="/new"       label="New"       icon={PlusIcon} />
            <TabItem to="/favorites" label="Favorites" icon={HeartIcon} />
            <TabItem to="/history"   label="History"   icon={ClockIcon} />
            <TabItem to="/profile"   label="Profile"   icon={UserIcon} />
          </div>
        </nav>
      </div>
    </div>
  );
}

/* ── Pieces ───────────────────────────────────────────── */

function Brand() {
  return (
    <div
      className="select-none leading-none"
      style={{ font: '500 24px/1 var(--f-text)', letterSpacing: '-0.01em' }}
    >
      <span style={{ color: 'var(--c-kivo-teal)' }}>kivo</span>
      <span style={{ color: 'var(--ink)' }}>.skin</span>
    </div>
  );
}

function SideItem({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `kv-nav-item flex items-center px-4 py-3 text-sm font-medium rounded-lg${isActive ? ' active' : ''}`
      }
    >
      <span className="material-icons-round mr-3 text-xl">{icon}</span>
      {label}
    </NavLink>
  );
}

function TabItem({
  to, label, icon: Icon
}: { to: string; label: string; icon: React.FC<{ active?: boolean }> }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({ color: isActive ? 'var(--c-kivo-teal)' : 'var(--ink-muted)' })}
      className="kv-tab-item flex flex-col items-center justify-center gap-1"
    >
      {({ isActive }) => (
        <>
          <Icon active={isActive} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

/* ── Minimal inline icons ─────────────────────────────── */

const HomeIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="stroke-current">
    <path d="M3 10.5 12 4l9 6.5V20a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2v-9.5Z"
      strokeWidth={active ? 2 : 1.7} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="stroke-current">
    <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"
      strokeWidth={active ? 2 : 1.7} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HeartIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="stroke-current">
    <path
      d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z"
      strokeWidth={active ? 2 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={active ? 'currentColor' : 'none'}
    />
  </svg>
);

const ClockIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="stroke-current">
    <circle cx="12" cy="12" r="9" strokeWidth={active ? 2 : 1.7} />
    <path d="M12 7v5l3 2" strokeWidth={active ? 2 : 1.7} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="stroke-current">
    <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm8 9a8 8 0 1 0-16 0"
      strokeWidth={active ? 2 : 1.7} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function AnimatedOutlet() {
  const element = useOutlet();
  const location = useLocation();
  const navType = useNavigationType();
  const isBack = navType === NavigationType.Pop;

  const variants: Variants = {
    initial: (back: boolean) => ({ x: back ? -24 : 24, opacity: 0 }),
    animate: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 } },
    exit:    (back: boolean) => ({ x: back ? 24 : -24, opacity: 0, transition: { duration: 0.18 } }),
  };

  return (
    <AnimatePresence mode="wait" initial={false} custom={isBack}>
      <motion.section
        key={location.pathname + location.search}
        custom={isBack}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="w-full min-w-0 max-w-full overflow-x-hidden rounded-2xl sm:rounded-3xl p-3 sm:p-5 md:p-8 transform-gpu will-change-transform [backface-visibility:hidden]"
        style={{ background: 'var(--c-cream-weak)', border: '1px solid var(--rule)' }}
      >
        {element}
      </motion.section>
    </AnimatePresence>
  );
}
